import { DEG, Mat3, RAD, Vec3, altAzToEnu, clamp, cross, mat3Apply, mat3T, norm, refractionLift } from '../lib/math';

export type Projection = 'perspective' | 'stereographic';

/**
 * View camera. `camToEnu` columns are the camera axes (right, up, back) expressed in ENU.
 * Field of view is measured across the shorter screen side.
 */
export class Camera {
  camToEnu: Mat3 = new Float64Array([1, 0, 0, 0, 0, -1, 0, 1, 0]);
  enuToCam: Mat3 = mat3T(this.camToEnu);
  fovDeg = 70;
  projection: Projection = 'stereographic';
  width = 1; // CSS pixels
  height = 1;
  private scale = 1;
  private fx = 1;
  private fy = 1;

  setViewport(w: number, h: number) {
    this.width = Math.max(1, w);
    this.height = Math.max(1, h);
    this.update();
  }

  setOrientation(camToEnu: Mat3) {
    this.camToEnu = camToEnu;
    this.enuToCam = mat3T(camToEnu);
  }

  /** Point the view centre at altitude/azimuth (degrees) with the horizon level. */
  lookAt(altDeg: number, azDeg: number, rollDeg = 0) {
    const f = altAzToEnu(clamp(altDeg, -89.999, 89.999), azDeg);
    let r = norm(cross(f, [0, 0, 1]));
    let u = cross(r, f);
    if (rollDeg) {
      const c = Math.cos(rollDeg * DEG), s = Math.sin(rollDeg * DEG);
      const r2: Vec3 = [r[0] * c + u[0] * s, r[1] * c + u[1] * s, r[2] * c + u[2] * s];
      u = [u[0] * c - r[0] * s, u[1] * c - r[1] * s, u[2] * c - r[2] * s];
      r = r2;
    }
    this.setOrientation(Float64Array.of(r[0], u[0], -f[0], r[1], u[1], -f[1], r[2], u[2], -f[2]));
  }

  update() {
    const half = (this.fovDeg * DEG) / 2;
    this.scale = this.projection === 'perspective' ? 1 / Math.tan(half) : 1 / (2 * Math.tan(half / 2));
    const portrait = this.width <= this.height;
    this.fx = portrait ? 1 : this.height / this.width;
    this.fy = portrait ? this.width / this.height : 1;
  }

  get uniforms() {
    return {
      proj: this.projection === 'perspective' ? 0 : 1,
      scale: this.scale,
      aspectFix: [this.fx, this.fy] as [number, number],
    };
  }

  /** Pixels per radian near the view centre (useful for sizing discs). */
  get pxPerRad() {
    const k = this.projection === 'perspective' ? 1 : 1; // both projections have unit slope at centre
    return (k * this.scale * Math.min(this.width, this.height)) / 2;
  }

  /** The view direction (ENU unit vector). */
  forward(): Vec3 {
    const m = this.camToEnu;
    return [-m[2], -m[5], -m[8]];
  }

  up(): Vec3 {
    const m = this.camToEnu;
    return [m[1], m[4], m[7]];
  }

  /**
   * Project an ENU direction (true, unrefracted unless `refracted`) to CSS pixels.
   * Returns null when the point is outside the projectable hemisphere.
   */
  project(enu: Vec3, out: { x: number; y: number } = { x: 0, y: 0 }, refracted = false): { x: number; y: number } | null {
    return this.projectXYZ(enu[0], enu[1], enu[2], out, refracted) ? out : null;
  }

  /** As project(), for a direction given by its parts, with nothing allocated (for loops over thousands of stars). */
  projectXYZ(x: number, y: number, z: number, out: { x: number; y: number }, refracted = false): boolean {
    if (!refracted) {
      // refractEnu, inline.
      const alt = Math.asin(clamp(z, -1, 1)) * RAD;
      const a2 = (alt + refractionLift(alt)) * DEG;
      const h = Math.hypot(x, y);
      if (h >= 1e-9) {
        const k = Math.cos(a2) / h;
        x *= k;
        y *= k;
        z = Math.sin(a2);
      }
    }
    const m = this.enuToCam;
    const c0 = m[0] * x + m[1] * y + m[2] * z;
    const c1 = m[3] * x + m[4] * y + m[5] * z;
    const c2 = m[6] * x + m[7] * y + m[8] * z;
    let px: number, py: number;
    if (this.projection === 'perspective') {
      if (c2 > -0.02) return false;
      px = c0 / -c2;
      py = c1 / -c2;
    } else {
      const den = 1 - c2;
      if (den < 0.03) return false;
      px = (2 * c0) / den;
      py = (2 * c1) / den;
    }
    const nx = px * this.scale * this.fx;
    const ny = py * this.scale * this.fy;
    out.x = ((nx + 1) / 2) * this.width;
    out.y = ((1 - ny) / 2) * this.height;
    return true;
  }

  /** Screen CSS pixels to an apparent (refracted) ENU direction. */
  unproject(x: number, y: number): Vec3 {
    const nx = (x / this.width) * 2 - 1;
    const ny = 1 - (y / this.height) * 2;
    const px = nx / (this.scale * this.fx);
    const py = ny / (this.scale * this.fy);
    let c: Vec3;
    if (this.projection === 'perspective') {
      c = norm([px, py, -1]);
    } else {
      const r = Math.hypot(px, py);
      const th = 2 * Math.atan(r / 2);
      const s = r > 1e-9 ? Math.sin(th) / r : 0;
      c = [px * s, py * s, -Math.cos(th)];
    }
    return mat3Apply(this.camToEnu, c[0], c[1], c[2]);
  }

  /** Is a CSS pixel position inside the viewport (with margin)? */
  onScreen(p: { x: number; y: number }, margin = 0) {
    return p.x >= -margin && p.y >= -margin && p.x <= this.width + margin && p.y <= this.height + margin;
  }
}
